import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, TrendingUp, TrendingDown, Users, Euro, 
  ShoppingCart, Award, BarChart3, Mail, Calendar, Crown, Trash2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  ResponsiveContainer, XAxis, YAxis, Tooltip, Legend, CartesianGrid 
} from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/');
      return;
    }
    fetchData();
  }, [user]);

  const fetchData = async () => {
    try {
      const headers = getAuthHeader();
      const [statsRes, usersRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers }),
        axios.get(`${API}/admin/users`, { headers })
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Supprimer le compte ${email} et tous ses projets ?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success(`${email} supprimé`);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#005F73] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-lg text-stone-600">Chargement des statistiques...</p>
        </div>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="min-h-screen bg-warm-sand flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-stone-600 mb-4">Erreur de chargement</p>
          <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
        </div>
      </div>
    );
  }

  const planColors = {
    starter: '#EE9B00',
    professional: '#005F73',
    free: '#94D2BD'
  };

  const planBadgeColors = {
    free: 'bg-stone-100 text-stone-700',
    starter: 'bg-blue-50 text-blue-700',
    professional: 'bg-purple-50 text-purple-700',
  };

  const formatDate = (val) => {
    if (!val) return '-';
    const d = new Date(val);
    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const revenueByPlanData = Object.entries(stats.revenue_by_plan).map(([plan, revenue]) => ({
    name: plan.charAt(0).toUpperCase() + plan.slice(1),
    revenue: revenue,
    color: planColors[plan] || '#94D2BD'
  }));

  return (
    <div className="min-h-screen bg-warm-sand py-12">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="mb-8">
          <Button
            data-testid="back-btn"
            variant="ghost"
            onClick={() => navigate('/')}
            className="mb-4 text-[#005F73] hover:text-[#005F73]/80"
          >
            <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
            Retour à l'accueil
          </Button>
          <h1 className="text-4xl md:text-5xl font-semibold text-[#005F73] mb-2 tracking-tight">
            Tableau de bord Admin
          </h1>
          <p className="text-lg text-stone-600">Vue d'ensemble de votre activité</p>
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Total Revenue */}
          <div 
            data-testid="metric-total-revenue"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">CA Total</span>
              <Euro className="w-5 h-5 text-[#0A9396]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#005F73]">
              {stats.total_revenue.toLocaleString()}€
            </div>
            <div className={`flex items-center gap-1 mt-2 text-sm ${stats.comparison.revenue_change >= 0 ? 'text-[#0A9396]' : 'text-[#AE2012]'}`}>
              {stats.comparison.revenue_change >= 0 ? (
                <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <TrendingDown className="w-4 h-4" strokeWidth={1.5} />
              )}
              <span>{Math.abs(stats.comparison.revenue_change)}% vs mois dernier</span>
            </div>
          </div>

          {/* Total Sales */}
          <div 
            data-testid="metric-total-sales"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">Nombre de ventes</span>
              <ShoppingCart className="w-5 h-5 text-[#EE9B00]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#005F73]">
              {stats.total_sales}
            </div>
            <div className={`flex items-center gap-1 mt-2 text-sm ${stats.comparison.sales_change >= 0 ? 'text-[#0A9396]' : 'text-[#AE2012]'}`}>
              {stats.comparison.sales_change >= 0 ? (
                <TrendingUp className="w-4 h-4" strokeWidth={1.5} />
              ) : (
                <TrendingDown className="w-4 h-4" strokeWidth={1.5} />
              )}
              <span>{Math.abs(stats.comparison.sales_change)}% vs mois dernier</span>
            </div>
          </div>

          {/* Average Basket */}
          <div 
            data-testid="metric-average-basket"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">Panier moyen</span>
              <BarChart3 className="w-5 h-5 text-[#94D2BD]" strokeWidth={1.5} />
            </div>
            <div className="text-3xl font-semibold text-[#005F73]">
              {stats.average_basket.toLocaleString()}€
            </div>
            <p className="text-sm text-stone-500 mt-2">Par transaction</p>
          </div>

          {/* Top Plan */}
          <div 
            data-testid="metric-top-plan"
            className="bg-white rounded-2xl border border-stone-100 p-6 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-stone-600 font-medium">Top Catégorie</span>
              <Award className="w-5 h-5 text-[#EE9B00]" strokeWidth={1.5} />
            </div>
            <div className="text-2xl font-semibold text-[#005F73] mb-1">
              {stats.top_plan.name}
            </div>
            <p className="text-sm text-stone-500">
              {stats.top_plan.revenue.toLocaleString()}€ • {stats.top_plan.sales} ventes
            </p>
          </div>
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Revenue Evolution */}
          <div 
            data-testid="chart-revenue-evolution"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <h2 className="text-2xl font-semibold text-[#005F73] mb-6">Évolution mensuelle du CA</h2>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={stats.monthly_evolution}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="month" 
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <Tooltip 
                  formatter={(value) => `${value}€`}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px' 
                  }}
                />
                <Legend />
                <Line 
                  type="monotone" 
                  dataKey="revenue" 
                  stroke="#005F73" 
                  strokeWidth={3}
                  name="Revenue (€)"
                  dot={{ fill: '#005F73', r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Revenue by Plan */}
          <div 
            data-testid="chart-revenue-by-plan"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <h2 className="text-2xl font-semibold text-[#005F73] mb-6">CA par catégorie</h2>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={revenueByPlanData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <YAxis 
                  tick={{ fontSize: 12 }}
                  stroke="#6b7280"
                />
                <Tooltip 
                  formatter={(value) => `${value}€`}
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px' 
                  }}
                />
                <Bar dataKey="revenue" name="Revenue (€)">
                  {revenueByPlanData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Detailed Tables */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sales by Plan */}
          <div 
            data-testid="table-sales-by-plan"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <h2 className="text-2xl font-semibold text-[#005F73] mb-6">Ventes par catégorie</h2>
            <div className="space-y-4">
              {Object.entries(stats.sales_by_plan).map(([plan, sales]) => (
                <div key={plan} className="flex items-center justify-between pb-3 border-b border-stone-200 last:border-0">
                  <span className="text-stone-700 font-medium capitalize">{plan}</span>
                  <span className="text-[#005F73] font-semibold">{sales} ventes</span>
                </div>
              ))}
            </div>
          </div>

          {/* Users by Plan */}
          <div 
            data-testid="table-users-by-plan"
            className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)]"
          >
            <h2 className="text-2xl font-semibold text-[#005F73] mb-6">Utilisateurs par plan</h2>
            <div className="space-y-4">
              {Object.entries(stats.users_by_plan).map(([plan, count]) => (
                <div key={plan} className="flex items-center justify-between pb-3 border-b border-stone-200 last:border-0">
                  <span className="text-stone-700 font-medium capitalize">{plan}</span>
                  <div className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-stone-400" strokeWidth={1.5} />
                    <span className="text-[#005F73] font-semibold">{count}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Users List */}
        <div 
          data-testid="users-list-section"
          className="bg-white rounded-2xl border border-stone-100 p-8 shadow-[0_4px_20px_-4px_rgba(0,95,115,0.1)] mt-8"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold text-[#005F73] flex items-center gap-2">
              <Users className="w-6 h-6" strokeWidth={1.5} />
              Utilisateurs inscrits ({users.length})
            </h2>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="users-table">
              <thead>
                <tr className="border-b-2 border-stone-200">
                  <th className="text-left py-3 px-3 text-stone-500 font-medium">Nom</th>
                  <th className="text-left py-3 px-3 text-stone-500 font-medium">Email</th>
                  <th className="text-left py-3 px-3 text-stone-500 font-medium">Plan</th>
                  <th className="text-left py-3 px-3 text-stone-500 font-medium">Projets</th>
                  <th className="text-left py-3 px-3 text-stone-500 font-medium">Inscription</th>
                  <th className="text-left py-3 px-3 text-stone-500 font-medium">Dernière connexion</th>
                  <th className="text-right py-3 px-3 text-stone-500 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-b border-stone-100 hover:bg-stone-50 transition-colors" data-testid={`user-row-${u.id}`}>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-stone-800">{u.full_name}</span>
                        {u.is_admin && <Crown className="w-4 h-4 text-[#EE9B00]" strokeWidth={1.5} />}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1.5 text-stone-600">
                        <Mail className="w-3.5 h-3.5 text-stone-400" strokeWidth={1.5} />
                        {u.email}
                      </div>
                    </td>
                    <td className="py-3 px-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${planBadgeColors[u.plan] || planBadgeColors.free}`}>
                        {u.plan}
                      </span>
                    </td>
                    <td className="py-3 px-3 text-stone-700 font-medium">{u.projects_count}</td>
                    <td className="py-3 px-3">
                      <div className="flex items-center gap-1.5 text-stone-600">
                        <Calendar className="w-3.5 h-3.5 text-stone-400" strokeWidth={1.5} />
                        {formatDate(u.created_at)}
                      </div>
                    </td>
                    <td className="py-3 px-3 text-stone-500">{formatDate(u.last_login_at)}</td>
                    <td className="py-3 px-3 text-right">
                      {!u.is_admin && (
                        <button
                          data-testid={`delete-user-${u.id}`}
                          onClick={() => deleteUser(u.id, u.email)}
                          className="text-red-400 hover:text-red-600 transition-colors p-1.5 rounded-lg hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
